import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import PointTypeBlock from './PointTypeBlock';
import ConfirmModal from './ConfirmModal';
import GemIcon from './icons/GemIcon';
import { PointType } from '../types/pointTypeManagement';
import { PointTypeManagementService } from '../services/pointTypeManagementService';
import { userDataService } from '../services/userDataService';
import { Class } from '../types/alarm';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface PointManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  classId: string;
  defaultPointValue: number;
  initialTab?: 'positive' | 'negative';
  onDataChanged?: () => void | Promise<void>; // 데이터 변경 시 콜백
}

const PointManagementModal: React.FC<PointManagementModalProps> = ({
  isOpen,
  onClose,
  userId,
  classId,
  defaultPointValue,
  initialTab = 'positive',
  onDataChanged
}) => {
  const [activeTab, setActiveTab] = useState<'positive' | 'negative'>(initialTab);
  const [positiveTypes, setPositiveTypes] = useState<PointType[]>([]);
  const [negativeTypes, setNegativeTypes] = useState<PointType[]>([]);
  const [initialPositiveTypes, setInitialPositiveTypes] = useState<PointType[]>([]);
  const [initialNegativeTypes, setInitialNegativeTypes] = useState<PointType[]>([]);
  const [editingType, setEditingType] = useState<PointType | null>(null);
  const [unconfirmedTempId, setUnconfirmedTempId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  /** 모달을 열었을 때 첫 로드가 끝나기 전까지 true → 깜빡임 방지 */
  const [hasLoadedForOpen, setHasLoadedForOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [pointTypeService, setPointTypeService] = useState<PointTypeManagementService | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<string | null>(null);
  
  // 다른 클래스 데이터 가져오기 관련 states
  const [showImportModal, setShowImportModal] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<Class[]>([]);
  const [selectedSourceClassId, setSelectedSourceClassId] = useState<string>('');
  const [sourcePositiveTypes, setSourcePositiveTypes] = useState<PointType[]>([]);
  const [sourceNegativeTypes, setSourceNegativeTypes] = useState<PointType[]>([]);
  const [selectedPositiveTypeIds, setSelectedPositiveTypeIds] = useState<Set<string>>(new Set());
  const [selectedNegativeTypeIds, setSelectedNegativeTypeIds] = useState<Set<string>>(new Set());
  const [isLoadingSourceTypes, setIsLoadingSourceTypes] = useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  
  // 다른 클래스 데이터 가져오기 버튼 활성화 상태 (기본값: off)
  const [isImportButtonEnabled, setIsImportButtonEnabled] = useState(false);

  // 로컬 스토리지 변경 감지
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      setIsImportButtonEnabled(saved !== null ? saved === 'true' : false);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('importButtonEnabledChanged', handleStorageChange);

    // 주기적으로 확인 (같은 탭에서의 변경 감지용)
    const interval = setInterval(() => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      const currentValue = saved !== null ? saved === 'true' : false;
      setIsImportButtonEnabled(prev => {
        if (prev !== currentValue) {
          return currentValue;
        }
        return prev;
      });
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('importButtonEnabledChanged', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // 드래그 앤 드롭 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 서비스 초기화
  useEffect(() => {
    if (userId && classId) {
      setPointTypeService(new PointTypeManagementService(userId, classId));
    }
  }, [userId, classId]);

  // 데이터 로드 (로드 완료 즉시 모달 표시 — 최소 지연 제거로 체감 속도 개선)
  const loadData = useCallback(async () => {
    if (!pointTypeService) return;
    
    setIsLoading(true);
    setUnconfirmedTempId(null);
    try {
      const data = await pointTypeService.getPointTypeData();
      setPositiveTypes(data.positiveTypes);
      setNegativeTypes(data.negativeTypes);
      setInitialPositiveTypes(JSON.parse(JSON.stringify(data.positiveTypes)));
      setInitialNegativeTypes(JSON.parse(JSON.stringify(data.negativeTypes)));
    } catch (error) {
      console.error('포인트 타입 데이터 로드 실패:', error);
    } finally {
      setIsLoading(false);
      setHasLoadedForOpen(true);
    }
  }, [pointTypeService]);

  // 변경 여부 확인
  const hasChanges = useCallback(() => {
    const posStr = (arr: PointType[]) => JSON.stringify(arr.map(t => ({ id: t.id, points: t.points, memo: t.memo })));
    const negStr = (arr: PointType[]) => JSON.stringify(arr.map(t => ({ id: t.id, points: t.points, memo: t.memo })));
    return posStr(positiveTypes) !== posStr(initialPositiveTypes) ||
           negStr(negativeTypes) !== negStr(initialNegativeTypes) ||
           positiveTypes.length !== initialPositiveTypes.length ||
           negativeTypes.length !== initialNegativeTypes.length;
  }, [positiveTypes, negativeTypes, initialPositiveTypes, initialNegativeTypes]);

  // 모달 닫을 때 "이번에 열었을 때 로드됨" 플래그 초기화
  useEffect(() => {
    if (!isOpen) {
      setHasLoadedForOpen(false);
    }
  }, [isOpen]);

  // 초기 데이터 로드
  useEffect(() => {
    if (isOpen && pointTypeService) {
      loadData();
    }
  }, [isOpen, pointTypeService, loadData]);

  // 다른 클래스 목록 로드
  const loadAvailableClasses = async () => {
    try {
      const allClasses = await userDataService.loadUserClasses(userId);
      // 현재 클래스 제외
      const filteredClasses = allClasses.filter(cls => cls.id !== classId);
      setAvailableClasses(filteredClasses);
    } catch (error) {
      console.error('클래스 목록 로드 실패:', error);
      setAvailableClasses([]);
    }
  };

  // 다른 클래스 데이터 가져오기 모달 열기
  const handleOpenImportModal = async () => {
    setShowImportModal(true);
    await loadAvailableClasses();
  };

  // 다른 클래스 데이터 가져오기 모달 닫기
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setSelectedSourceClassId('');
    setSourcePositiveTypes([]);
    setSourceNegativeTypes([]);
    setSelectedPositiveTypeIds(new Set());
    setSelectedNegativeTypeIds(new Set());
    setIsClassDropdownOpen(false);
  };

  // 소스 클래스 변경 핸들러
  const handleSourceClassChange = async (sourceClassId: string) => {
    setSelectedSourceClassId(sourceClassId);
    setIsClassDropdownOpen(false);
    setIsLoadingSourceTypes(true);
    
    try {
      const sourceService = new PointTypeManagementService(userId, sourceClassId);
      const sourceData = await sourceService.getPointTypeData();
      
      // getPointTypeData()에서 이미 날짜 변환이 완료되었으므로 그대로 사용
      const positiveTypes = sourceData.positiveTypes || [];
      const negativeTypes = sourceData.negativeTypes || [];
      
      console.log('🔍 로드된 긍정행동 포인트:', positiveTypes);
      console.log('🔍 로드된 행동체크 포인트:', negativeTypes);
      console.log('🔍 긍정행동 포인트 개수:', positiveTypes.length);
      console.log('🔍 행동체크 포인트 개수:', negativeTypes.length);
      
      setSourcePositiveTypes(positiveTypes);
      setSourceNegativeTypes(negativeTypes);
      setSelectedPositiveTypeIds(new Set());
      setSelectedNegativeTypeIds(new Set());
    } catch (error) {
      console.error('소스 클래스 포인트 타입 로드 실패:', error);
      setSourcePositiveTypes([]);
      setSourceNegativeTypes([]);
    } finally {
      setIsLoadingSourceTypes(false);
    }
  };

  // 긍정행동 포인트 타입 선택 토글
  const handleTogglePositiveTypeSelection = (typeId: string) => {
    const newSelected = new Set(selectedPositiveTypeIds);
    if (newSelected.has(typeId)) {
      newSelected.delete(typeId);
    } else {
      newSelected.add(typeId);
    }
    setSelectedPositiveTypeIds(newSelected);
  };

  // 행동체크 포인트 타입 선택 토글
  const handleToggleNegativeTypeSelection = (typeId: string) => {
    const newSelected = new Set(selectedNegativeTypeIds);
    if (newSelected.has(typeId)) {
      newSelected.delete(typeId);
    } else {
      newSelected.add(typeId);
    }
    setSelectedNegativeTypeIds(newSelected);
  };

  // 선택한 포인트 타입 적용 (로컬에만 임시 추가)
  const handleApplySelectedTypes = () => {
    for (const typeId of Array.from(selectedPositiveTypeIds)) {
      const sourceType = sourcePositiveTypes.find(t => t.id === typeId);
      if (sourceType) {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const createdType: PointType = {
          ...sourceType,
          id: tempId,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setPositiveTypes(prev => [...prev, createdType]);
      }
    }

    for (const typeId of Array.from(selectedNegativeTypeIds)) {
      const sourceType = sourceNegativeTypes.find(t => t.id === typeId);
      if (sourceType) {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const createdType: PointType = {
          ...sourceType,
          id: tempId,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setNegativeTypes(prev => [...prev, createdType]);
      }
    }

    handleCloseImportModal();
  };

  // initialTab이 변경되면 activeTab 업데이트
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // 새 포인트 타입 추가 (로컬에만 임시 저장)
  const handleAddPointType = () => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const createdType: PointType = {
      id: tempId,
      points: defaultPointValue,
      memo: '새 포인트 타입',
      category: activeTab,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    if (activeTab === 'positive') {
      setPositiveTypes(prev => [...prev, createdType]);
    } else {
      setNegativeTypes(prev => [...prev, createdType]);
    }
    setEditingType(createdType);
    setUnconfirmedTempId(tempId);
  };

  // 포인트 타입 수정
  const handleEditPointType = (pointType: PointType) => {
    setEditingType(pointType);
  };

  // 포인트 타입 저장 (로컬에만 반영, 서버 저장은 [저장] 버튼 클릭 시)
  const handleSavePointType = (pointType: PointType) => {
    if (activeTab === 'positive') {
      setPositiveTypes(prev => prev.map(p => p.id === pointType.id ? pointType : p));
    } else {
      setNegativeTypes(prev => prev.map(p => p.id === pointType.id ? pointType : p));
    }
    setEditingType(null);
    if (pointType.id === unconfirmedTempId) {
      setUnconfirmedTempId(null);
    }
  };

  // 포인트 타입 삭제 확인
  const handleDeletePointType = (pointTypeId: string) => {
    setTypeToDelete(pointTypeId);
    setIsDeleteConfirmOpen(true);
  };

  // 포인트 타입 삭제 실행 (로컬에만 반영)
  const executeDeletePointType = () => {
    if (!typeToDelete) return;

    if (activeTab === 'positive') {
      setPositiveTypes(prev => prev.filter(p => p.id !== typeToDelete));
    } else {
      setNegativeTypes(prev => prev.filter(p => p.id !== typeToDelete));
    }

    setIsDeleteConfirmOpen(false);
    setTypeToDelete(null);
  };

  // 수정 취소 (금방 추가해 아직 확인 안 한 temp 블럭만 닫기 시 목록에서 제거)
  const handleCancelEdit = () => {
    if (editingType?.id === unconfirmedTempId) {
      if (activeTab === 'positive') {
        setPositiveTypes(prev => prev.filter(p => p.id !== editingType.id));
      } else {
        setNegativeTypes(prev => prev.filter(p => p.id !== editingType.id));
      }
      setUnconfirmedTempId(null);
    }
    setEditingType(null);
  };

  // 드래그 종료 핸들러 (로컬에만 반영)
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const currentTypes = activeTab === 'positive' ? positiveTypes : negativeTypes;
    const oldIndex = currentTypes.findIndex(type => type.id === active.id);
    const newIndex = currentTypes.findIndex(type => type.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newTypes = arrayMove(currentTypes, oldIndex, newIndex);

    if (activeTab === 'positive') {
      setPositiveTypes(newTypes);
    } else {
      setNegativeTypes(newTypes);
    }
  };

  // 전체 저장 (서버에 반영)
  const handleSaveAll = async () => {
    if (!pointTypeService) return;

    setIsSaving(true);
    try {
      const posToSave = [...positiveTypes];
      const negToSave = [...negativeTypes];

      // 1. 삭제: 초기에는 있었으나 현재 없는 항목 (서버에 있는 것만)
      const toDeletePos = initialPositiveTypes.filter(p => !posToSave.find(t => t.id === p.id)).map(p => p.id);
      const toDeleteNeg = initialNegativeTypes.filter(p => !negToSave.find(t => t.id === p.id)).map(p => p.id);

      for (const id of toDeletePos) await pointTypeService.deletePointType(id);
      for (const id of toDeleteNeg) await pointTypeService.deletePointType(id);

      // 2. 추가: temp_ 로 시작하는 항목 → 서버에 생성 후 id 매핑
      const tempIdMap: Record<string, string> = {};
      for (const type of posToSave) {
        if (type.id.startsWith('temp_')) {
          const { id: _id, createdAt, updatedAt, ...rest } = type;
          const newId = await pointTypeService.createPointType(rest);
          tempIdMap[type.id] = newId;
        }
      }
      for (const type of negToSave) {
        if (type.id.startsWith('temp_')) {
          const { id: _id, createdAt, updatedAt, ...rest } = type;
          const newId = await pointTypeService.createPointType(rest);
          tempIdMap[type.id] = newId;
        }
      }

      // 3. 수정: 초기와 다른 항목 (temp 제외)
      for (const type of posToSave) {
        if (!type.id.startsWith('temp_')) {
          const initial = initialPositiveTypes.find(p => p.id === type.id);
          if (initial && (initial.points !== type.points || initial.memo !== type.memo)) {
            await pointTypeService.updatePointType(type.id, { points: type.points, memo: type.memo });
          }
        }
      }
      for (const type of negToSave) {
        if (!type.id.startsWith('temp_')) {
          const initial = initialNegativeTypes.find(p => p.id === type.id);
          if (initial && (initial.points !== type.points || initial.memo !== type.memo)) {
            await pointTypeService.updatePointType(type.id, { points: type.points, memo: type.memo });
          }
        }
      }

      // 4. 순서 저장: temp id를 실제 id로 치환한 최종 배열
      const finalPos: PointType[] = posToSave.map(t => 
        t.id.startsWith('temp_') && tempIdMap[t.id]
          ? { ...t, id: tempIdMap[t.id] }
          : t
      );
      const finalNeg: PointType[] = negToSave.map(t =>
        t.id.startsWith('temp_') && tempIdMap[t.id]
          ? { ...t, id: tempIdMap[t.id] }
          : t
      );

      if (finalPos.length > 0) await pointTypeService.reorderPointTypes('positive', finalPos);
      if (finalNeg.length > 0) await pointTypeService.reorderPointTypes('negative', finalNeg);

      // 5. 서버에서 최종 데이터 로드
      const fresh = await pointTypeService.getPointTypeData();
      setPositiveTypes(fresh.positiveTypes);
      setNegativeTypes(fresh.negativeTypes);
      setInitialPositiveTypes(JSON.parse(JSON.stringify(fresh.positiveTypes)));
      setInitialNegativeTypes(JSON.parse(JSON.stringify(fresh.negativeTypes)));
      setEditingType(null);

      if (onDataChanged) await onDataChanged();
    } catch (error) {
      console.error('포인트 타입 저장 실패:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // 모달 닫기 (변경 시 확인)
  const handleClose = () => {
    if (hasChanges()) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  const currentTypes = activeTab === 'positive' ? positiveTypes : negativeTypes;

  /* 로드 완료 전에는 모달 박스 없이 오버레이 + 로딩만 표시 → 로드 완료 후 모달 표시로 깜빡임 제거 */
  if (!hasLoadedForOpen || isLoading) {
    return (
      <ModalOverlay onClick={onClose}>
        <FullScreenLoadingWrap onClick={(e) => e.stopPropagation()}>
          <LoadingText>로딩 중...</LoadingText>
        </FullScreenLoadingWrap>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>포인트 타입별 세트 설정</h3>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* 탭 네비게이션 */}
          <TabContainer>
            <TabButton
              $active={activeTab === 'positive'}
              onClick={() => setActiveTab('positive')}
            >
              <TabButtonText>
                <span>긍정행동</span>
                <span>포인트</span>
              </TabButtonText>
            </TabButton>
            <TabButton
              $active={activeTab === 'negative'}
              onClick={() => setActiveTab('negative')}
            >
              <TabButtonText>
                <span>행동체크</span>
                <span>포인트</span>
              </TabButtonText>
            </TabButton>
          </TabContainer>

          {/* 포인트 타입 목록 */}
          <PointTypesContainer>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={currentTypes.map(type => type.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {currentTypes.map(pointType => (
                    <PointTypeBlock
                      key={pointType.id}
                      pointType={pointType}
                      onEdit={handleEditPointType}
                      onDelete={handleDeletePointType}
                      isEditing={editingType?.id === pointType.id}
                      onSave={handleSavePointType}
                      onCancel={handleCancelEdit}
                    />
                  ))}
                </SortableContext>
              </DndContext>
          </PointTypesContainer>

          {/* 새 세트 추가 버튼 */}
          <AddButton onClick={handleAddPointType} disabled={editingType !== null}>
            <AddIcon>+</AddIcon>
            새 세트 추가
          </AddButton>

          {/* 다른 클래스 데이터 가져오기 버튼 */}
          {isImportButtonEnabled && (
            <ImportButton onClick={handleOpenImportModal} disabled={editingType !== null}>
              다른 클래스 데이터 가져오기
            </ImportButton>
          )}
        </ModalBody>

        {/* 하단 [닫기] [저장] 버튼 */}
        <ModalFooter>
          <ModalFooterButton data-variant="modal-secondary" onClick={handleClose}>
            닫기
          </ModalFooterButton>
          <ModalFooterSaveButton onClick={handleSaveAll} disabled={isSaving}>
            {isSaving ? '저장 중...' : '저장'}
          </ModalFooterSaveButton>
        </ModalFooter>
      </ModalContent>

    {/* 닫기 확인 모달 (변경 사항 있을 때) */}
    {showCloseConfirm && (
      <ConfirmModal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={handleConfirmClose}
        title={['저장하지 않은 변경 사항이 있습니다.', '닫으시겠습니까?']}
        confirmText="닫기"
        cancelText="취소"
        icon="?"
        iconBackground="var(--color-warning)"
      />
    )}

    {/* 삭제 확인 모달 */}
    {isDeleteConfirmOpen && (
      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setTypeToDelete(null);
        }}
        onConfirm={executeDeletePointType}
        title={[
          "이 포인트 블럭을 삭제할까요?"
        ]}
        confirmText="삭제"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-exempted)"
      />
    )}

    {/* 다른 클래스 데이터 가져오기 모달 */}
    {showImportModal && (
      <ImportModalOverlay onClick={handleCloseImportModal}>
        <ImportModalContent onClick={(e) => e.stopPropagation()}>
          <ImportModalHeader>
            <ImportModalTitle>다른 클래스 데이터 가져오기</ImportModalTitle>
            <ImportModalCloseButton onClick={handleCloseImportModal}>×</ImportModalCloseButton>
          </ImportModalHeader>
            
          <ImportModalBody>
            {/* 클래스 선택 드롭다운 */}
            <ImportSection>
              <ImportLabel>클래스 선택</ImportLabel>
              <ImportClassSelectContainer>
                <ImportClassSelectButton
                  onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                  type="button"
                >
                  <ImportClassSelectText>
                    {selectedSourceClassId 
                      ? availableClasses.find(c => c.id === selectedSourceClassId)?.name || '클래스를 선택하세요'
                      : '클래스를 선택하세요'}
                  </ImportClassSelectText>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ImportClassSelectButton>
                {isClassDropdownOpen && (
                  <>
                    <ImportClassDropdownOverlay onClick={() => setIsClassDropdownOpen(false)} />
                    <ImportClassDropdown>
                      <ImportClassDropdownOption
                        onClick={() => {
                          setSelectedSourceClassId('');
                          setIsClassDropdownOpen(false);
                          setSourcePositiveTypes([]);
                          setSourceNegativeTypes([]);
                          setSelectedPositiveTypeIds(new Set());
                          setSelectedNegativeTypeIds(new Set());
                        }}
                      >
                        클래스를 선택하세요
                      </ImportClassDropdownOption>
                      {availableClasses.map(cls => (
                        <ImportClassDropdownOption
                          key={cls.id}
                          onClick={() => handleSourceClassChange(cls.id)}
                        >
                          {cls.name}
                        </ImportClassDropdownOption>
                      ))}
                    </ImportClassDropdown>
                  </>
                )}
              </ImportClassSelectContainer>
            </ImportSection>
            
            {/* 포인트 타입 목록 */}
            {isLoadingSourceTypes ? (
              <ImportLoadingText>포인트 타입을 불러오는 중...</ImportLoadingText>
            ) : selectedSourceClassId && sourcePositiveTypes.length === 0 && sourceNegativeTypes.length === 0 ? (
              <ImportEmptyText>선택한 클래스에 포인트 타입이 없습니다.</ImportEmptyText>
            ) : selectedSourceClassId ? (
              <>
                {/* 긍정행동 포인트 타입 */}
                <ImportSection>
                  <ImportLabel>
                    긍정행동 포인트
                  </ImportLabel>
                  {sourcePositiveTypes.length > 0 ? (
                    <ImportTypeGrid>
                      {sourcePositiveTypes.map(type => {
                        const isSelected = selectedPositiveTypeIds.has(type.id);
                        return (
                          <ImportTypeBlock
                            key={type.id}
                            $isSelected={isSelected}
                            onClick={() => handleTogglePositiveTypeSelection(type.id)}
                          >
                            <ImportTypeIconContainer>
                              <GemIcon color="red" size={20} />
                            </ImportTypeIconContainer>
                            <ImportTypeInfo>
                              <ImportTypeName>{type.memo}</ImportTypeName>
                              <ImportTypePoints>+{type.points}P</ImportTypePoints>
                            </ImportTypeInfo>
                            {isSelected && (
                              <ImportCheckIcon>✓</ImportCheckIcon>
                            )}
                          </ImportTypeBlock>
                        );
                      })}
                    </ImportTypeGrid>
                  ) : (
                    <ImportEmptyBlock>
                      등록된 긍정행동 블럭이 없습니다
                    </ImportEmptyBlock>
                  )}
                </ImportSection>

                {/* 행동체크 포인트 타입 */}
                <ImportSection>
                  <ImportLabel>
                    행동체크 포인트
                  </ImportLabel>
                  {sourceNegativeTypes.length > 0 ? (
                    <ImportTypeGrid>
                      {sourceNegativeTypes.map(type => {
                        const isSelected = selectedNegativeTypeIds.has(type.id);
                        return (
                          <ImportTypeBlock
                            key={type.id}
                            $isSelected={isSelected}
                            onClick={() => handleToggleNegativeTypeSelection(type.id)}
                          >
                            <ImportTypeIconContainer>
                              <GemIcon color="gray" size={20} />
                            </ImportTypeIconContainer>
                            <ImportTypeInfo>
                              <ImportTypeName>{type.memo}</ImportTypeName>
                              <ImportTypePoints>{type.points}P</ImportTypePoints>
                            </ImportTypeInfo>
                            {isSelected && (
                              <ImportCheckIcon>✓</ImportCheckIcon>
                            )}
                          </ImportTypeBlock>
                        );
                      })}
                    </ImportTypeGrid>
                  ) : (
                    <ImportEmptyBlock>
                      등록된 행동체크 블럭이 없습니다
                    </ImportEmptyBlock>
                  )}
                </ImportSection>
              </>
            ) : null}
          </ImportModalBody>
          
          <ImportModalFooter>
            <ImportModalButton onClick={handleCloseImportModal} $variant="secondary">
              닫기
            </ImportModalButton>
            <ImportModalButton
              onClick={handleApplySelectedTypes}
              $variant="primary"
              disabled={selectedPositiveTypeIds.size === 0 && selectedNegativeTypeIds.size === 0}
            >
              <span className="button-text-full">확인</span>
              <span className="button-text-short">확인</span>
            </ImportModalButton>
          </ImportModalFooter>
        </ImportModalContent>
      </ImportModalOverlay>
    )}
  </ModalOverlay>
);
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 3000; /* 포인트 타입 선택 모달(z-index: 2001)보다 높게 설정 */
  overflow-y: auto;
  padding: 60px 20px;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  margin: 0 auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-gray);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  flex: 1;
`;

const TabContainer = styled.div`
  display: flex;
  background: var(--color-border);
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 24px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 8px 16px;
  border: none;
  background: ${props => props.$active ? 'var(--color-surface)' : 'transparent'};
  color: ${props => props.$active ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  
  /* 다크모드: 선택한 탭의 텍스트를 흰색으로 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$active ? '#ffffff' : 'var(--color-text-muted)'};
  }
`;

const TabButtonText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;

  span {
    display: inline;
  }

  @media (max-width: 420px) {
    flex-direction: column;
    gap: 0;
    line-height: 1.2;

    span {
      display: block;
    }
  }
`;

/** 로드 완료 전 전체 화면 로딩 (모달 박스 없이 표시) */
const FullScreenLoadingWrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* 클릭은 아래 오버레이로 전달 */
`;

const ModalBodyLoadingWrap = styled.div`
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PointTypesContainer = styled.div`
  margin-bottom: 24px;
`;

const LoadingText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  padding: 40px 0;
`;

const AddButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px dashed var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgba(var(--color-accent-rgb), 0.5);
    color: var(--color-accent-text);
    background: var(--color-accent-soft);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
`;

const ModalFooterButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const ModalFooterSaveButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--color-accent-strong);
  color: white;
  border: none;

  &:hover:not(:disabled) {
    background: var(--color-accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
`;

const ImportButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin-top: 12px;
  border: 2px dashed var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgba(var(--color-accent-rgb), 0.5);
    color: var(--color-accent-text);
    background: var(--color-accent-soft);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 다른 클래스 데이터 가져오기 모달 스타일
const ImportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10000;
  overflow-y: auto;
  padding: 60px 20px;
`;

const ImportModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  margin: 0 auto;
`;

const ImportModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const ImportModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
`;

const ImportModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

const ImportModalBody = styled.div`
  padding: 24px;
`;

const ImportSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ImportLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 12px;
`;

const ImportEmptyBlock = styled.div`
  width: 100%;
  padding: 16px;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 14px;
  background: var(--color-surface);
  margin-top: 12px;
`;

const ImportClassSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ImportClassSelectButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-accent-text);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  svg {
    flex-shrink: 0;
    color: var(--color-text-secondary);
  }
`;

const ImportClassSelectText = styled.span`
  flex: 1;
  text-align: left;
`;

const ImportClassDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const ImportClassDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  margin-top: 4px;
  overflow: visible;
`;

const ImportClassDropdownOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: var(--color-text-primary);
  text-align: left;
  
  &:hover {
    background-color: var(--color-panel);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`;

const ImportTypeGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const ImportTypeBlock = styled.div<{ $isSelected: boolean }>`
  position: relative;
  width: 100%;
  padding: 12px 16px;
  min-height: 44px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 8px;
  background: ${props => props.$isSelected ? 'rgba(16, 80, 74, 0.05)' : 'var(--color-surface)'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  &:hover {
    border-color: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-text)'};
    background: ${props => props.$isSelected ? 'rgba(16, 80, 74, 0.1)' : 'var(--color-panel)'};
  }
`;

const ImportTypeIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ImportTypeInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ImportTypeName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
`;

const ImportTypePoints = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-accent-strong);
`;

const ImportCheckIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`;

const ImportLoadingText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  padding: 40px 0;
  font-size: 14px;
`;

const ImportEmptyText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  padding: 40px 0;
  font-size: 14px;
`;

const ImportModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
`;

const ImportModalButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  .button-text-full {
    display: inline;
  }

  .button-text-short {
    display: none;
  }

  @media (max-width: 480px) {
    .button-text-full {
      display: none;
    }

    .button-text-short {
      display: inline;
    }
  }
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    
    &:disabled {
      background: var(--color-border);
      color: var(--color-text-muted);
      cursor: not-allowed;
      opacity: 0.5;
    }
  ` : `
    background: var(--color-border);
    color: var(--color-text-primary);
    
    &:hover {
      background: var(--color-text-muted);
      color: white;
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

export default PointManagementModal;
